// Install record (Implementation Plan v02 §6.1; data-map.md): a small CLEAR
// record with no content — versions, channel, last update check, last backup,
// and the failed-unlock counter WITHOUT times (IR-22). Readable before unlock so
// the wrong-try delay survives a reload.

import { META_INSTALL, type StoreDb, watchDone } from './schema'

export interface InstallRecord {
  readonly appVersion: string
  readonly channel: string
  /** ISO date of the last update check, or null. */
  readonly lastUpdateCheck: string | null
  /** ISO date of the last encrypted backup, or null. */
  readonly lastBackup: string | null
  /** Failed unlock tries since the last success. No times are kept. */
  readonly failedUnlocks: number
}

export function newInstallRecord(appVersion: string, channel: string): InstallRecord {
  return { appVersion, channel, lastUpdateCheck: null, lastBackup: null, failedUnlocks: 0 }
}

function isInstallRecord(value: unknown): value is InstallRecord {
  return typeof value === 'object' && value !== null && 'failedUnlocks' in value && 'appVersion' in value
}

export async function readInstallRecord(db: StoreDb): Promise<InstallRecord | null> {
  const value = await db.get('meta', META_INSTALL)
  return isInstallRecord(value) ? value : null
}

export async function writeInstallRecord(db: StoreDb, record: InstallRecord): Promise<void> {
  await db.put('meta', record, META_INSTALL)
}

/** Count one failed unlock; returns the new count. */
export async function recordFailedUnlock(db: StoreDb): Promise<number> {
  const tx = db.transaction('meta', 'readwrite')
  const done = watchDone(tx)
  const current = await tx.store.get(META_INSTALL)
  const base = isInstallRecord(current) ? current : newInstallRecord('unknown', 'unknown')
  const next = { ...base, failedUnlocks: base.failedUnlocks + 1 }
  await tx.store.put(next, META_INSTALL)
  await done
  return next.failedUnlocks
}

/** The day a backup file was made (I-1a Batch 6b): a date only, no time. */
export async function recordBackupMade(db: StoreDb, day: string): Promise<void> {
  const tx = db.transaction('meta', 'readwrite')
  const done = watchDone(tx)
  const current = await tx.store.get(META_INSTALL)
  const base = isInstallRecord(current) ? current : newInstallRecord('unknown', 'unknown')
  await tx.store.put({ ...base, lastBackup: day }, META_INSTALL)
  await done
}

/** Called after a successful unlock (the count is shown to the person first). */
export async function resetFailedUnlocks(db: StoreDb): Promise<void> {
  const tx = db.transaction('meta', 'readwrite')
  const done = watchDone(tx)
  const current = await tx.store.get(META_INSTALL)
  if (isInstallRecord(current) && current.failedUnlocks !== 0) {
    await tx.store.put({ ...current, failedUnlocks: 0 }, META_INSTALL)
  }
  await done
}
